<template>
  <LoaderWrapper ref="dashboardLoader" class="h-100 row">
  <div v-for="store in stores" :key="store.id" class="col-sm-6">
    <div class="card card-body me-2 ms-2">
      <small v-if="!store.isActive" class="text-warning"><i class="fa fa-warning"></i> {{helper.trans('labels.store_not_active')}}</small>
      <div v-if="isAdmin" class="d-flex flex-column">
        <template v-for="(limit, option) in store.payInRequestLimits">
          <small v-if="limit < 1" class="text-danger">
            <i class="fa fa-warning"></i>
            <span class="ms-1" v-if="option !== 'any'">{{helper.trans(`payment_options.${option}`)}}: </span>
            {{helper.trans('labels.payInRequestLimitPercentWarning', {percent: numberFormatter.formatNumber(helper.floatToPercent(limit), 0)})}}
          </small>
        </template>
      </div>

      <div class="d-flex justify-content-between gap-3 flex-wrap">
        <div class="d-flex flex-column p-0">
          <div class="text-capitalize-first">
            <div class="text-secondary" v-if="isAdmin">
              <small v-b-tooltip.hover :title="store.id">{{ store.id }}</small> <copy-button :value="store.id"></copy-button>
            </div>
            <div>
              <strong class="text-nowrap text-capitalize-first">{{ store.name }} ({{ store.currency }})</strong>
              <span v-if="isAdmin">
                <small v-for="group in store.trafficGroups" :key="group.id" class="badge bg-dark text-light ms-2">
                    {{ group._trafficGroup?.type }}: {{ group._trafficGroup?.name }}
                  <span
                    v-if="group._trafficGroup?.publicName"> ({{ group._trafficGroup?.publicName }})
                  </span>
                </small>
              </span>
            </div>
          </div>
          <div>
            <span v-if="isAdmin && store.isPayInBlocked" class="me-2">in: <i class="fa fa-circle-pause text-warning"></i></span>
            <span v-if="isAdmin && store.isPayOutBlocked" class="me-2">out: <i class="fa fa-circle-pause text-warning"></i></span>
            <span v-if="isAdmin && !store.isWebhooksEnabled" class="me-2">webhook: <i class="fa fa-circle-pause text-warning"></i></span>
          </div>
          <small>{{ helper.trans("labels.service_fee") }}: {{ numberFormatter.formatNumber(store.payInFee * 100, 2) }}% / {{ numberFormatter.formatNumber(store.payOutFee * 100, 2) }}%</small>
          <small v-if="isAdmin && store.payInRateSource">in: {{helper.trans(`rate_sources.${store.payInRateSource}`) }}</small>
          <small v-if="isAdmin && store.payOutRateSource">out: {{helper.trans(`rate_sources.${store.payOutRateSource}`) }}</small>
        </div>
        <div v-if="isAdmin || isMerchant || isOperator" class="d-flex gap-2 align-items-center ps-1">
          <button type="button" @click="showMerchantStoreEditModal(store)" v-b-tooltip.hover :title="helper.trans('button.edit')" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize"><i class="fa fa-pencil"></i></button>
          <button v-if="isAdmin" type="button" @click="deleteMerchantStore(store)" v-b-tooltip.hover :title="helper.trans('button.delete')" class="btn btn-sm btn-outline-danger text-nowrap text-capitalize"><i class="fa fa-trash"></i></button>
        </div>
      </div>

      <div>
        <div class="invoice-input-group mt-4">
          <div class="input-group input-group-sm">
            <span class="input-group-text bg-white" id="addon-wrapping">{{ helper.trans("labels.api_key")}}</span>
            <input type="text" class="form-control invoice-input font-monospace" :value="store.apiKey" readonly>
            <copy-button class="btn btn-copy" :value="store.apiKey"></copy-button>
          </div>
        </div>
      </div>

      <div v-for="storeAccount in store.accounts" class=" mt-3">
        <div class="d-flex justify-content-between gap-3 flex-wrap">
          <AccountWidget show-id show-icon hide-insurance :account="storeAccount.account"></AccountWidget>
          <div class="d-flex gap-2 align-items-center ps-1">
            <div class="btn-group" v-if="isMerchant || (isMerchantOperator && storeAccount.account.withdrawalWhitelistsEnabled)">
              <button type="button" @click.prevent="withdrawAccount = storeAccount.account" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize"><i class="fa-solid fa-upload"></i> {{ helper.trans("button.withdraw") }}</button>
              <button v-if="!isMerchantOperator" type="button" class="btn btn-sm btn-outline-dark dropdown-toggle dropdown-toggle-split" data-bs-toggle="dropdown" aria-expanded="false">
                <span class="visually-hidden"></span>
              </button>
              <ul class="dropdown-menu" v-if="!isMerchantOperator">
                <li>
                  <a class="dropdown-item text-capitalize-first" href="#" @click.prevent="withdrawalWhitelistModal?.show(storeAccount.account);">{{helper.trans('labels.edit_whitelist')}}</a>
                </li>
                <li>
                  <a class="dropdown-item text-capitalize-first" href="#" @click.prevent="autoWithdrawalSettingsModal?.show(storeAccount.account)">{{helper.trans('labels.edit_auto_withdrawal')}}</a>
                </li>
                <li>
                  <a class="dropdown-item text-capitalize-first" href="#" @click.prevent="autoTransferSettingsModal?.show(storeAccount.account)">{{helper.trans("labels.auto_transfer_settings")}}</a>
                </li>
                <li>
                  <a class="dropdown-item text-capitalize-first" href="#" @click.prevent="withdrawMerchantTransfer?.show(storeAccount.account)">{{ helper.trans("button.transfer") }}</a>
                </li>
              </ul>
              <button v-if="isMerchantOperator" type="button" class="btn btn-sm btn-outline-dark dropdown-toggle dropdown-toggle-split" data-bs-toggle="dropdown" aria-expanded="false">
                <span class="visually-hidden"></span>
              </button>
              <ul class="dropdown-menu" v-if="isMerchantOperator">
                <li>
                  <a class="dropdown-item text-capitalize-first" href="#" @click.prevent="withdrawalWhitelistModal?.show(storeAccount.account);">{{helper.trans('labels.show_whitelist')}}</a>
                </li>
              </ul>
            </div>


            <div class="btn-group" v-if="isMerchantOperator">
              <button type="button" @click.prevent="withdrawMerchantTransfer?.show(storeAccount.account)" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize"><i class="fa-solid fa-upload"></i> {{ helper.trans("button.transfer") }}</button>
            </div>

            <button @click.prevent="accountWalletModal?.show(storeAccount.account)" type="button" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize"><i class="fa-solid fa-download"></i> {{ helper.trans("button.deposit") }}</button>

            <button v-if="canBalanceCorrection" @click.prevent="openBalanceCorrection(storeAccount, store)" type="button" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize"><i class="fa fa-calculator"></i> {{ helper.trans("button.balance_correction") }}</button>
          </div>
        </div>
        <div class="text-end">
          <small class="text-danger" style="font-size: 70%;" v-if="storeAccount.account.isAutoWithdrawalEnabled &&  storeAccount.account.withdrawalWhitelistsEnabled">
            {{ helper.trans('labels.auto_withdrawal_enabled', {
            address: storeAccount.account.autoWithdrawalAddress,
            sum: storeAccount.account.autoWithdrawalMinSum
                ? numberFormatter.formatMoney(storeAccount.account.autoWithdrawalMinSum, 0)
                : ''
          }) }}
          </small>
          <small class="text-danger" style="font-size: 70%;" v-if="storeAccount.account.isAutoTransferEnabled">
            {{ helper.trans('labels.auto_transfer_enabled', {
            sum: storeAccount.account.autoTransferMinSum
                ? numberFormatter.formatMoney(storeAccount.account.autoTransferMinSum, 0)
                : '',
            store: getTargetStoreName(storeAccount.account.autoTransferTargetAccountId)
          }) }}
          </small>
        </div>
      </div>

      <div class="d-flex gap-2 align-items-center justify-content-end mt-3">
        <button
          type="button"
          class="btn btn-sm btn-outline-dark text-nowrap text-capitalize-first"
          @click="selectStore(store, {name: 'accountOperations', query: {storeId: store.id}})"
        >
          {{ helper.trans("menu.items.accountOperations") }}
        </button>

        <button
          type="button"
          class="btn btn-sm btn-outline-dark text-nowrap text-capitalize"
          @click="selectStore(store, {name: 'invoices', query: {storeId: store.id}})"
        >
          {{ helper.trans("menu.items.invoices") }}
        </button>
      </div>

    </div>
  </div>

  <div class="col-sm-6 d-flex">
    <div @click.prevent="showMerchantStoreEditModal({})" style="cursor: pointer" class="card card-body me-2 ms-2 text-center d-flex justify-content-center align-items-center store-create-button">
      <i style="font-size: 150px" class="fa fa-plus"></i>
    </div>
  </div>

    <b-modal dialog-class="modal-lg"
             :title="merchantStoreModalTitle"
             :modelValue="merchantStoreModal"
             body-class="p-0"
             @update:modelValue="merchantStoreModal = $event"
    >
      <template #default>
        <MerchantStoreForm ref="merchantForm" @open-settings-history="showStoreHistoryFromForm" @open-cross-border-rates="showCrossBorderRates" @open-referral-settings="showReferralFees" @save="saveMerchantStore"></MerchantStoreForm>
      </template>
      <template #footer>
        <button type="button" v-on:click="merchantStoreModal = false" class="btn btn-outline-secondary btn-sm text-capitalize-first">{{ helper.trans('button.cancel') }}</button>
        <LoaderWrapper ref="loaderCreateFrom" :zoom="0.5">
          <button type="button" @click="saveMerchantStore()" class="btn btn-outline-primary btn-sm text-capitalize-first">{{ helper.trans('button.save') }}</button>
        </LoaderWrapper>
      </template>
    </b-modal>

    <SettingsHistoryModal
      ref="storeHistoryModal"
      entity-type="store"
    />

    <CrossBorderRatesModal
      ref="crossBorderRatesModal"
      @apply="applyCrossBorderRates"
    />

    <ReferralStoreFeesModal
      ref="referralFeesModal"
      @apply="applyReferralFees"
    />

    <b-modal :title="helper.trans('labels.withdrawal')" :modelValue="withdrawAccount !== null" header-class="text-capitalize" body-class="pt-0" footer-class="d-flex justify-content-between" @update:modelValue="$event === false && (withdrawAccount = null)">
      <template #default>
        <WithdrawalForm ref="withdrawalForm" :account="withdrawAccount"></WithdrawalForm>
      </template>
      <template #footer>
        <button type="button" v-on:click="withdrawAccount = null" class="btn btn-outline-dark btn-sm text-capitalize-first">{{ helper.trans("button.cancel") }}</button>
        <LoaderWrapper ref="loaderWithdrawalForm" :zoom="0.5">
          <button type="button" v-on:click="createWithdraw()" class="btn btn-outline-primary btn-sm text-capitalize-first">{{ helper.trans("button.withdraw") }}</button>
        </LoaderWrapper>
      </template>
    </b-modal>

    <AccountWalletModal ref="accountWalletModal"></AccountWalletModal>
    <ConfirmModal ref="confirmModal"></ConfirmModal>
    <WithdrawalWhitelistModal ref="withdrawalWhitelistModal" @updated="update()"></WithdrawalWhitelistModal>
    <AutoWithdrawalSettingsModal @account:updated="update()" ref="autoWithdrawalSettingsModal"></AutoWithdrawalSettingsModal>
    <AutoTransferSettingsModal :stores="stores" @account:updated="update()" ref="autoTransferSettingsModal"></AutoTransferSettingsModal>
    <MerchantTransferModal ref="withdrawMerchantTransfer" @updated="update()"></MerchantTransferModal>

    <BalanceCorrectionModal
      :showModal="balanceCorrectionModal"
      :accountId="balanceCorrectionAccount?.id"
      :entityName="balanceCorrectionStoreName"
      entityType="store"
      @update:showModal="balanceCorrectionModal = $event"
      @success="update()"
    />
  </LoaderWrapper>
</template>

<script lang="ts">

import {computed, defineComponent, onMounted, Prop, PropType, reactive, ref, toRefs} from 'vue'
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import CopyButton from "@/components/CopyButton.vue";
import MerchantStoreDTO from "@/models/MerchantStoreDTO";
import {RouteLocationRaw} from "vue-router";
import MerchantStoreFormDTO, {CrossBorderSettingRowFormDTO, MerchantStoreTrafficGroupFormDTO, ReferralStoreSettingRowFormDTO} from "@/models/form/MerchantStoreFormDTO";
import PaginationDTO from "@/models/PaginationDTO";
import TrafficGroupDTO from "@/models/TrafficGroupDTO";
import router from "@/router";
import {useStore} from "vuex";
import MerchantStoreForm from "@/components/Form/MerchantStoreForm.vue";
import SettingsHistoryModal from "@/components/SettingsHistoryModal.vue";
import CrossBorderRatesModal from "@/components/CrossBorderRatesModal.vue";
import ReferralStoreFeesModal from "@/components/ReferralStoreFeesModal.vue";
import ConfirmModal, {ConfirmModalProps} from "@/components/ConfirmModal.vue";
import {useNotification} from "@kyvg/vue3-notification";
import MerchantDTO from "@/models/MerchantDTO";
import AccountDTO from "@/models/AccountDTO";
import WithdrawalForm from "@/components/Form/WithdrawalForm.vue";
import AccountWidget from "@/components/AccountWidget.vue";
import AccountWalletModal from "@/components/AccountWalletModal.vue";
import WithdrawalWhitelistModal from "@/components/WithdrawalWhitelistModal.vue";
import AutoWithdrawalSettingsModal from "@/components/AutoWithdrawalSettingsModal.vue";
import AutoTransferSettingsModal from "@/components/AutoTransferSettingsModal.vue";
import TransferForm from "@/components/Form/TransferForm.vue";
import MerchantTransferModal from "@/components/MerchantTransferModal.vue";
import BalanceCorrectionModal from "@/components/BalanceCorrectionModal.vue";
import MerchantStoreAccountDTO from "@/models/MerchantStoreAccountDTO";
import {UserPermission} from "@/models/enum/UserPermission";
import {FlowType} from "@/models/enum/FlowType";
import {Dictionary} from "@/types";

const api = new APIClient();
const notification = useNotification()
const defaultMerchantStoreForm = {
  id: null,
  name: null,
  currency: null,
  isActive: false,
  payInFee: null,
  payInFeeByPaymentOption: [],
  payOutFee: null,
  merchantId: null,
  minPayInAmount: null,
  maxPayInAmount: null,
  minPayOutAmount: null,
  maxPayOutAmount: null,
  payOutRateSource: null,
  payInRateSource: null,
  payInRateShift: '0',
  trafficGroups: [],
  isPayInBlocked: false,
  isPayOutBlocked: false,
  isWebhooksEnabled: true,
  isExpiredInvoiceWebhookEnabled: false,
  isStrictPayOutDealLifetime: false,
  isDeeplinksEnabled: false,
  isTrxIdRequiredForConfirm: false,
  hideUploadReceipt: false,
  receiptPdfRequired: false,
  showTpayWarning: false,
  vietQrMobileSaveFlow: false,
  vietQrVndAmountWarning: false,
  requestUserPhone: false,
  stripRequisitesSpaces: false,
  paymentPageSettings: {banks: [], layout: null},
  reusablePaymentsCountLimit: null,
  dealTimeOutMin: null,
  payInRequestLimits: [],
  payInRequestLimitsByAmount: [],
  disputeOpeningPeriod: null,
  isDisputeReopenAllowed: true,
  isDisputeAmountChecked: false,
  isDisputeOnCompletedDealsAllowed: false,
  isDisputeOpeningByApiDisabled: false,
  isAdminOnlyDisputeOnCompleted: false,
  isAutoDisputeOnExpireEnabled: false,
  isCancelOnDisputeAutocloseEnabled: false,
  isExpiredOnDealTimeoutEnabled: false,
  isUserWhitelistEnabled: false,
  userWhitelistText: '',
  isAutoDisputeOnExpireReceiptRequired: false,
  autoDisputeOnExpirePeriod: null,
  disputeAutoclosePeriod: null,
  payOutDealAcceptTimeout: null,
  payOutDealDisputeTimeout: 'PT30M',
  payOutDealLifetimeTimeout: null,
  payOutDealSla: '',
  isSingleTakePayOutEnabled: false,
  allowedPaymentOptions: [],
  allowedPaymentMethod: [],
  blacklistPaymentMethods: [],
  allowedPayoutMethods: [],
  isPayoutHolderNameHidden: null,
  isOnlySingleConcurrentDealAllowedForUser: false,
  allowDuplicateInvoices: null,
  nightShiftHours: [],
  signatureRequired: true,
  maxBoundSumForUniqueSums: '0',
  payOutFixedRate: null,
  traderPayOutRateSource: null,
  traderPayInRateSource: null,
  flowType: FlowType.DEFAULT,
  defaultPaymentOption: null,
  defaultPaymentSystem: null,
  config: null,
  issuanceTimeAlertEnabled: false,
  issuanceTimeAlertCriticalMilliseconds: null,
  issuanceTimeAlertTgChatId: null,
  crossBorderSettings: [],
} as MerchantStoreFormDTO

export default defineComponent({
  components: {
    MerchantTransferModal,
    BalanceCorrectionModal,
    TransferForm,
    AutoWithdrawalSettingsModal,
    AutoTransferSettingsModal,
    WithdrawalWhitelistModal,
    AccountWidget,
    LoaderWrapper,
    CopyButton,
    MerchantStoreForm,
    SettingsHistoryModal,
    CrossBorderRatesModal,
    ReferralStoreFeesModal,
    ConfirmModal,
    WithdrawalForm,
    AccountWalletModal,
  },
  props: {
    merchant: {
      type: Object as PropType<MerchantDTO>,
      default: null
    } as Prop<MerchantDTO>,
    stores: {
      type: Object as PropType<MerchantStoreDTO[]>,
      default: null
    } as Prop<MerchantStoreDTO[]>
  },
  emits: ['updated'],
  setup(props, { emit }) {
    const store = useStore()

    const dashboardLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const merchantForm = ref<InstanceType<typeof MerchantStoreForm> | null>(null)
    const confirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null)
    const loaderCreateFrom = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const withdrawalForm = ref<InstanceType<typeof WithdrawalForm> | null>(null)
    const loaderWithdrawalForm = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const withdrawalWhitelistModal = ref<InstanceType<typeof WithdrawalWhitelistModal> | null>(null)
    const autoWithdrawalSettingsModal = ref<InstanceType<typeof AutoWithdrawalSettingsModal> | null>(null)
    const autoTransferSettingsModal = ref<InstanceType<typeof AutoTransferSettingsModal> | null>(null)
    const withdrawMerchantTransfer = ref<InstanceType<typeof MerchantTransferModal> | null>(null)
    const accountWalletModal = ref<InstanceType<typeof AccountWalletModal> | null>(null)
    const storeHistoryModal = ref<InstanceType<typeof SettingsHistoryModal> | null>(null)
    const crossBorderRatesModal = ref<InstanceType<typeof CrossBorderRatesModal> | null>(null)
    const referralFeesModal = ref<InstanceType<typeof ReferralStoreFeesModal> | null>(null)

    const state = reactive({
      withdrawAccount: null as AccountDTO|null,
      depositAccount: null as AccountDTO|null,
      transferAccount: null as AccountDTO|null,
      merchantStoreModal: false,
      merchantStoreModalTitle: "",
      balanceCorrectionModal: false,
      balanceCorrectionAccount: null as AccountDTO|null,
      balanceCorrectionStoreName: "",
    });

    const createWithdraw = async () => {
      loaderWithdrawalForm.value?.show()
      withdrawalForm.value?.save().then(() => {
        state.withdrawAccount = null;
        loaderWithdrawalForm.value?.hide()
        emit('updated')
      }).catch(() => {
        loaderWithdrawalForm.value?.hide()
      }).finally(() => {
        loaderWithdrawalForm.value?.hide()
      })

    }

    const selectStore = (merchantStore: MerchantStoreDTO, to: RouteLocationRaw) => {
      store.dispatch('merchantStores/setCurrentStore', merchantStore)
      router.push(to)
    }

    const showMerchantStoreEditModal = async (dto: MerchantStoreDTO) => {
      if (dto.id) {
        let storeDto = dto;
        dashboardLoader.value?.show();
        try {
          storeDto = await api.storeGet(dto.id);
          const trafficGroupIds = storeDto.trafficGroups.map((item) => item.id);
          if (trafficGroupIds.length > 0) {
            const trafficGroups = (await api.call.trafficGroups.list({limit: trafficGroupIds.length, offset: 0} as PaginationDTO, {ids: trafficGroupIds})).items;
            const trafficGroupsById = {} as Dictionary<TrafficGroupDTO>;
            for (const group of trafficGroups) {
              trafficGroupsById[group.id] = group;
            }
            for (const item of storeDto.trafficGroups) {
              item._trafficGroup = trafficGroupsById[item.id] ?? null;
            }
          }
        } catch (e) {
          notification.notify({
            type: 'error',
            title: helper.trans('labels.error'),
            text: e instanceof Error ? e.message : String(e),
          });
          return;
        } finally {
          dashboardLoader.value?.hide();
        }

        let limits = [];
        for (const index in storeDto.payInRequestLimits) {
          limits.push({
            option: index === 'any' ? null : index,
            value: helper.floatToPercent(storeDto.payInRequestLimits[index])
          })
        }

        state.merchantStoreModalTitle = helper.trans('labels.edit_store', {name: storeDto.name})
        merchantForm.value?.setForm({
          ...defaultMerchantStoreForm,
          ...storeDto,
          payInRequestLimits: limits,
          payInRequestLimitsByAmount: (storeDto.payInRequestLimitsByAmount ?? []).map((range) => ({
            minSum: range.minSum !== null && range.minSum !== undefined ? (Number(range.minSum) / 100).toString() : '',
            maxSum: range.maxSum !== null && range.maxSum !== undefined ? (Number(range.maxSum) / 100).toString() : '',
            value: helper.floatToPercent(range.limit).toString(),
          })),
          payInFee: storeDto.payInFee ? (Math.round(storeDto.payInFee * 1000000) / 10000).toString() : null,
          payInFeeByPaymentOption: Object.entries(storeDto.payInFeeByPaymentOption ?? {}).map(([option, fee]) => ({
            option,
            feePercent: helper.floatToPercent(fee).toString()
          })),
          payOutFee: storeDto.payOutFee ? (Math.round(storeDto.payOutFee * 1000000) / 10000).toString() : null,
          payInRateShift: helper.floatToPercent(storeDto.payInRateShift).toString(),
          payOutFixedRate: storeDto.payOutFixedRateSum?.amount,
          reusablePaymentsCountLimit: storeDto.reusablePaymentsCountLimit === null || storeDto.reusablePaymentsCountLimit === undefined
            ? null
            : storeDto.reusablePaymentsCountLimit.toString(),
          minPayInAmount: storeDto.minPayInAmount !== null ? parseInt(storeDto.minPayInAmount).toString() : null,
          maxPayInAmount: storeDto.maxPayInAmount !== null ? parseInt(storeDto.maxPayInAmount).toString() : null,
          minPayOutAmount: storeDto.minPayOutAmount !== null ? parseInt(storeDto.minPayOutAmount).toString() : null,
          maxPayOutAmount: storeDto.maxPayOutAmount !== null ? parseInt(storeDto.maxPayOutAmount).toString() : null,
          trafficGroups: storeDto.trafficGroups.map((item) => {
            return {
              trafficGroup: item._trafficGroup,
              fee: item.fee  ? helper.floatToPercent(item.fee).toString() : null,
              feeByDealAmount: item.feeByDealAmount ? item.feeByDealAmount.map(range => ({
                minSum: range.minSum !== null && range.minSum !== undefined ? (range.minSum / 100).toFixed(2) : null,
                maxSum: range.maxSum !== null && range.maxSum !== undefined ? (range.maxSum / 100).toFixed(2) : null,
                fee: range.fee ? range.fee.toString() : null,
                _feePercent: range.fee ? helper.floatToPercent(range.fee.toString()) : null
              })) : []
            }
          }) as MerchantStoreTrafficGroupFormDTO[],
          maxBoundSumForUniqueSums: storeDto.maxBoundSumForUniqueSums.amount,
          config: storeDto.config,
          isUserWhitelistEnabled: storeDto.isUserWhitelistEnabled ?? false,
          userWhitelistText: storeDto.userWhitelist?.length ? JSON.stringify(storeDto.userWhitelist, null, 2) : '',
          payOutDealSla: storeDto.payOutDealSla ?? '',
        } as MerchantStoreFormDTO)
      } else {
        merchantForm.value?.setForm({
          ...defaultMerchantStoreForm,
          merchantId: props.merchant?.id,
          merchant: props.merchant ?? null,
        } as MerchantStoreFormDTO)
        state.merchantStoreModalTitle = helper.trans('labels.create_store')
      }
      state.merchantStoreModal = true;
    }

    const refreshMerchantStoreApiKey = async (store: MerchantStoreDTO) => {
      dashboardLoader.value?.show();
        await api.updateStoreApiKey(store.id)
      dashboardLoader.value?.hide();
      emit('updated')
    }

    const deleteMerchantStore = async (store: MerchantStoreDTO) => {
      confirmModal.value?.show({
        title: helper.trans('labels.store_delete_confirmation', {name: store.name}),
        okTitle: helper.trans(`button.delete`),
        cancelTitle: null,
        onOkClick: async () => {
          dashboardLoader.value?.show();
          await api.deleteStore(store.id)
          dashboardLoader.value?.hide();
          emit('updated')
        }
      } as ConfirmModalProps);
    }

    const saveMerchantStore = () => {
      loaderCreateFrom.value?.show()

      const form = merchantForm.value!.getForm()
      const promise = merchantForm.value!.save();

      if (promise === null) {
        loaderCreateFrom.value?.hide();
        return;
      }

      if (form.id) {
        promise
          ?.then((resp) => {
            notification.notify({title: helper.trans('store.notice.updated')});
          })
      } else {
        promise
            ?.then((resp) => {
            notification.notify({title: helper.trans('store.notice.created')});
          })
      }

      promise
          ?.then(async (resp) => {
          state.merchantStoreModal = false;
          emit('updated')
        })
        .catch((e) => {
          if (e?.response?.status === 400 && e?.response?.data) {
            merchantForm.value?.handleSaveErrors(e.response.data);
          }
        })
        .finally(() => {
          loaderCreateFrom.value?.hide();
        })
    }

    const update = () =>  {
      emit('updated')
    }

    const openBalanceCorrection = (storeAccount: MerchantStoreAccountDTO, merchantStore: MerchantStoreDTO) => {
      state.balanceCorrectionAccount = storeAccount.account
      state.balanceCorrectionStoreName = merchantStore.name
      state.balanceCorrectionModal = true
    }

    const getTargetStoreName = (targetAccountId: string|null) => {
      if (!targetAccountId || !props.stores) return ''

      // Ищем аккаунт по ID во всех магазинах
      for (const store of props.stores) {
        const account = store.accounts.find(acc => acc.account.id === targetAccountId)
        if (account) {
          return store.name
        }
      }
      return ''
    }

    const showCrossBorderRates = () => {
      const form = merchantForm.value?.getForm()
      crossBorderRatesModal.value?.show(
        form?.crossBorderSettings ?? [],
        merchantForm.value?.getCrossBorderErrors() ?? [],
        form?.id ?? null,
        form?.name ?? '',
      )
    }

    const applyCrossBorderRates = (rows: CrossBorderSettingRowFormDTO[]) => {
      merchantForm.value?.setCrossBorderSettings(rows)
    }

    const showReferralFees = () => {
      const form = merchantForm.value?.getForm()
      referralFeesModal.value?.show(
        form?.referralSettings ?? [],
        merchantForm.value?.getReferralErrors() ?? [],
      )
    }

    const applyReferralFees = (rows: ReferralStoreSettingRowFormDTO[]) => {
      merchantForm.value?.setReferralSettings(rows)
    }

    const showStoreHistoryFromForm = () => {
      const form = merchantForm.value?.getForm()
      if (!form?.id) {
        return
      }

      storeHistoryModal.value?.show(
        form.id,
        helper.trans('labels.settings_history') + `: ${form.name}`,
      )
    }

    onMounted(() => {
      if (store.getters.isAdmin) {
        store.dispatch('trafficGroups/loadList')
      }
    });

    return {
      ...toRefs(state),
      dashboardLoader,
      loaderCreateFrom,
      merchantForm,
      confirmModal,
      dateHelper,
      numberFormatter,
      helper,
      saveMerchantStore,
      refreshMerchantStoreApiKey,
      showMerchantStoreEditModal,
      deleteMerchantStore,
      selectStore,
      isAdmin: computed<boolean>(() => store.getters.isAdmin),
      canBalanceCorrection: computed<boolean>(() => store.getters.isAdmin && store.getters.hasPermission(UserPermission.BALANCE_CORRECTION)),
      openBalanceCorrection,
      isOperator: computed<boolean>(() => store.getters.isOperator),
      isMerchant: computed<boolean>(() => store.getters.isMerchant),
      isMerchantOperator: computed<boolean>(() => store.getters.isMerchantOperator),
      withdrawalForm,
      loaderWithdrawalForm,
      createWithdraw,
      withdrawalWhitelistModal,
      autoWithdrawalSettingsModal,
      autoTransferSettingsModal,
      accountWalletModal,
      withdrawMerchantTransfer,
      update,
      getTargetStoreName,
      showStoreHistoryFromForm,
      storeHistoryModal,
      showCrossBorderRates,
      applyCrossBorderRates,
      crossBorderRatesModal,
      showReferralFees,
      applyReferralFees,
      referralFeesModal,
    }
  }
})
</script>
